<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { helpers, sameAs } from "@vuelidate/validators";
import { useValidators } from "@/composables/useValidators";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import CnmcDialog from "./CnmcDialog.vue";
import { computed, nextTick, ref, toRaw, useId, watch } from "vue";
import type { Account, Company } from "@/types";
import WizardFooter from "@/components/common/WizardFooter.vue";
import type { CnmcFilingProfile } from "./cnmcTypes";
import { parseCnmcAddress } from "./cnmcAddress";

const props = defineProps<{
  profile: CnmcFilingProfile | null;
  company: Company;
  account?: Pick<Account, "name" | "email" | "phone_number"> | null;
  connectionId?: number | null;
  producerNif?: string | null;
  busy: boolean;
}>();
const emit = defineEmits<{ save: [profile: CnmcFilingProfile] }>();
const visible = defineModel<boolean>({ required: true });
const id = useId();
const normalizedCountry = computed(() =>
  /^[a-z]{2}$/i.test(props.company.country || "")
    ? props.company.country.toUpperCase()
    : "ES"
);
const defaults = (): CnmcFilingProfile => ({
  producer_name: props.company.name || "",
  street: "",
  street_number: "",
  staircase: "",
  postal_code: "",
  municipality: "",
  province: "",
  country:
    new Intl.DisplayNames(["es"], { type: "region" }).of(
      normalizedCountry.value
    ) || "España",
  phone: props.account?.phone_number || "",
  email: props.company.contact_email || props.account?.email || "",
  place: "",
  representative_name: props.account?.name || "",
  representation_confirmed: false,
  declarations_accepted: false,
  attached_documents: "",
  ...(normalizedCountry.value === "ES"
    ? parseCnmcAddress(props.company.address)
    : {}),
});
const draft = ref<CnmcFilingProfile>(defaults());
const sections = [
  { title: "producer", fields: ["producer_name"] },
  {
    title: "contact_address",
    fields: [
      "email",
      "phone",
      "street",
      "street_number",
      "staircase",
      "postal_code",
      "municipality",
      "province",
      "country",
    ],
  },
  { title: "representation", fields: ["representative_name", "place"] },
] as const;
const { t } = useI18n();
const validators = useValidators(t);
const optionalFields = ["staircase", "representative_name"];
const maxLengths: Partial<Record<keyof CnmcFilingProfile, number>> = {
  producer_name: 128,
  street: 200,
  street_number: 20,
  postal_code: 20,
  municipality: 100,
  province: 100,
  phone: 40,
  place: 100,
};
const accepted = helpers.withMessage(
  () => t("validations.required"),
  sameAs(true)
);
const rules = {
  ...Object.fromEntries(
    sections.flatMap((section) =>
      section.fields.map((field) => [
        field,
        {
          ...(optionalFields.includes(field)
            ? {}
            : { required: validators.required }),
          ...(maxLengths[field]
            ? { maxLength: validators.maxLength(maxLengths[field]) }
            : {}),
          ...(field === "email" ? { email: validators.email } : {}),
        },
      ])
    )
  ),
  representation_confirmed: { accepted },
  declarations_accepted: { accepted },
};
const v$ = useVuelidate(rules, draft, {
  $autoDirty: true,
  $stopPropagation: true,
});
const save = () => {
  v$.value.$touch();
  if (!props.busy && !v$.value.$invalid) emit("save", draft.value);
};
watch(
  visible,
  async (open) => {
    if (open) {
      draft.value = props.profile
        ? structuredClone(toRaw(props.profile))
        : defaults();
      await nextTick();
      v$.value.$reset();
    }
  },
  { immediate: true }
);
</script>

<template>
  <CnmcDialog
    v-model="visible"
    :title="$t('cnmc.filing_details')"
    :eyebrow="
      connectionId ? `${$t('cnmc.connection')} ${connectionId}` : undefined
    "
    :busy
  >
    <form
      :id="`${id}-form`"
      class="p-3 p-md-4"
      novalidate
      @submit.prevent="save"
    >
      <p class="small text-body-secondary mb-4">
        {{ $t("cnmc.filing_snapshot_help") }}
      </p>
      <section
        v-for="section in sections"
        :key="section.title"
        class="py-0 mb-4"
      >
        <h5 class="eyebrow-label mb-3">{{ $t(`cnmc.${section.title}`) }}</h5>
        <p
          v-if="section.title === 'contact_address' && company.address"
          class="small text-body-secondary mb-3"
        >
          {{ $t("company.address") }}: {{ company.address }}
        </p>
        <div class="row g-3">
          <div
            v-if="section.title === 'producer' && producerNif"
            class="col-md-6"
          >
            <label :for="`${id}-nif`" class="form-label">NIF</label>
            <input
              :id="`${id}-nif`"
              :value="producerNif"
              class="form-control bg-body-tertiary"
              readonly
            />
            <div class="form-text">{{ $t("cnmc.connection_identity") }}</div>
          </div>
          <div v-for="field in section.fields" :key="field" class="col-md-6">
            <label :for="`${id}-${field}`" class="form-label"
              >{{ $t(`cnmc.fields.${field}`)
              }}<span
                v-if="!optionalFields.includes(field)"
                class="text-danger ms-1"
                aria-hidden="true"
                >*</span
              ></label
            >
            <input
              :id="`${id}-${field}`"
              v-model.trim="draft[field]"
              :type="
                field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'
              "
              class="form-control"
              :class="{ 'is-invalid': v$[field].$error }"
              :aria-invalid="v$[field].$error"
              @blur="v$[field].$touch()"
              :disabled="busy"
            />
            <CnmcFieldErrors :validation="v$[field]" />
          </div>
        </div>
      </section>
      <section class="border-top pt-4 pb-0">
        <h5 class="eyebrow-label mb-3">{{ $t("cnmc.declarations") }}</h5>
        <div class="form-check mb-3">
          <input
            :id="`${id}-representation`"
            v-model="draft.representation_confirmed"
            type="checkbox"
            class="form-check-input"
            :class="{ 'is-invalid': v$.representation_confirmed.$error }"
            :aria-invalid="v$.representation_confirmed.$error"
            @blur="v$.representation_confirmed.$touch()"
            :disabled="busy"
          />
          <label :for="`${id}-representation`" class="form-check-label small">{{
            $t("cnmc.confirm_representation")
          }}</label>
          <CnmcFieldErrors :validation="v$.representation_confirmed" />
        </div>
        <div class="form-check">
          <input
            :id="`${id}-declarations`"
            v-model="draft.declarations_accepted"
            type="checkbox"
            class="form-check-input"
            :class="{ 'is-invalid': v$.declarations_accepted.$error }"
            :aria-invalid="v$.declarations_accepted.$error"
            @blur="v$.declarations_accepted.$touch()"
            :disabled="busy"
          />
          <label :for="`${id}-declarations`" class="form-check-label small">{{
            $t("cnmc.accept_declarations")
          }}</label>
          <CnmcFieldErrors :validation="v$.declarations_accepted" />
        </div>
      </section>
    </form>
    <template #footer>
      <WizardFooter class="w-100">
        <template #start
          ><span class="small text-body-secondary">{{
            $t("cnmc.saved_for_connection")
          }}</span></template
        >
        <template #end>
          <button
            class="btn btn-link"
            :disabled="busy"
            @click="visible = false"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            type="submit"
            :form="`${id}-form`"
            class="btn btn-primary"
            :disabled="busy"
          >
            {{ $t("common.save") }}
          </button>
        </template>
      </WizardFooter>
    </template>
  </CnmcDialog>
</template>
